// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, Element, Fragment, h, Prop } from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes } from '../../helpers'
import type { Importance, Size, Status } from '../shared'

export type TagStatus = Status | 'neutral'

/**
 * The `<qds-tag>` element is a static descriptor used for items that need to
 * be labeled, categorized, or organized using keywords that describe them.
 *
 * @see https://quartz.se.com/build/components/tag
 */
@Component({
  tag: 'qds-tag',
  shadow: true,
  styleUrl: 'tag.css',
})
export class Tag implements ComponentInterface {
  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The tag's importance.
   */
  @Prop() public readonly importance?: Importance = 'standard'

  /**
   * The tag's status.
   */
  @Prop() public readonly status?: TagStatus = 'neutral'

  /**
   * The tag's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The tag's text.
   */
  @Prop() public readonly text?: string

  /**
   * Provides a text description of the icon for screen readers.
   */
  @Prop() public readonly iconDescription?: string

  @Element() private readonly host!: HTMLElement

  #inheritedAttributes: Attributes = {}

  get #computedImportance(): Importance {
    switch (this.importance) {
      case 'subdued':
      case 'emphasized': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedStatus(): TagStatus {
    switch (this.status) {
      case 'error':
      case 'info':
      case 'success':
      case 'warning': {
        return this.status
      }
      default: {
        return 'neutral'
      }
    }
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)
  }

  public render() {
    return (
      <span
        class="qds-tag"
        data-importance={this.#computedImportance}
        data-size={this.#computedSize}
        data-status={this.#computedStatus}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.#inheritedAttributes}
      >
        {this.iconName !== undefined && this.iconName !== '' && (
          <>
            {this.iconDescription !== undefined &&
              this.iconDescription !== '' && (
                <span class="qds-sr-only">{this.iconDescription}</span>
              )}
            <qds-icon
              aria-hidden="true"
              class="qds-icon"
              data-size={this.#computedSize}
              library={this.iconLibrary}
              name={this.iconName}
            />
          </>
        )}
        <span class="qds-text">{this.text}</span>
      </span>
    )
  }
}
